import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  LinearProgress,
  Fade,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';

function Categorias() {
  const theme = useTheme();
  const [categorias, setCategorias] = useState([]);
  const [lanchonetes, setLanchonetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    lanchonete_id: ''
  });
  const { isAdminMaster, getLanchoneteId } = useAuth();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const lanchoneteId = isAdminMaster() ? '' : getLanchoneteId();

      const promises = [
        isAdminMaster()
          ? api.get('/categorias')
          : api.get(`/categorias?lanchonete_id=${lanchoneteId}`)
      ];

      if (isAdminMaster()) {
        promises.push(api.get('/lanchonetes'));
      }

      const [categoriasResponse, lanchonetesResponse] = await Promise.all(promises);
      
      setCategorias(categoriasResponse.data);
      if (lanchonetesResponse) {
        setLanchonetes(lanchonetesResponse.data);
      }
      
      if (!isAdminMaster() && lanchoneteId) {
        setFormData(prev => ({ ...prev, lanchonete_id: lanchoneteId }));
      }
    } catch (error) {
      toast.error('Erro ao carregar categorias');
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdminMaster, getLanchoneteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDialog = (categoria) => {
    setEditingCategoria(categoria);
    const defaultLanchoneteId = isAdminMaster() ? '' : getLanchoneteId();

    if (categoria) {
      setFormData({
        name: categoria.name,
        description: categoria.description || '',
        lanchonete_id: categoria.lanchonete_id
      });
    } else {
      setFormData({
        name: '',
        description: '',
        lanchonete_id: defaultLanchoneteId
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategoria(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCategoria) {
        await api.put(`/categorias/${editingCategoria.id}`, {
          name: formData.name,
          description: formData.description
        });
        toast.success('Categoria atualizada com sucesso!');
      } else {
        await api.post('/categorias', {
          name: formData.name,
          description: formData.description,
          lanchonete_id: formData.lanchonete_id
        });
        toast.success('Categoria criada com sucesso!');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria? Os produtos associados poderão ficar sem categoria.')) {
      try {
        await api.delete(`/categorias/${id}`);
        toast.success('Categoria excluída com sucesso!');
        loadData();
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        toast.error('Erro ao excluir categoria');
      }
    }
  };

  const getLanchoneteName = (lanchoneteId) => {
    const lanchonete = lanchonetes.find(l => l.id === lanchoneteId);
    return lanchonete ? lanchonete.name : 'Lanchonete não encontrada';
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.contrastText,
                mb: 2
              }} 
            />
            <Typography variant="h6" color="inherit">
              Carregando categorias...
            </Typography>
          </Box>
        </Fade>
        <LinearProgress 
          sx={{ 
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Categorias de Produtos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog(null)}
        >
          Nova Categoria
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome da Categoria</TableCell>
              <TableCell>Descrição</TableCell>
              {isAdminMaster() && <TableCell>Lanchonete</TableCell>}
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categorias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdminMaster() ? 4 : 3} align="center">
                  <Box py={3}>
                    <CategoryIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                    <Typography color="textSecondary">
                      Nenhuma categoria personalizada encontrada.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              categorias.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {cat.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {cat.description || '-'}
                    </Typography>
                  </TableCell>
                  {isAdminMaster() && (
                    <TableCell>
                      <Typography variant="body2">
                        {getLanchoneteName(cat.lanchonete_id)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(cat)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(cat.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="name"
                label="Nome da Categoria"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="description"
                label="Descrição"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
              />
              
              {isAdminMaster() && !editingCategoria && (
                <FormControl fullWidth required>
                  <InputLabel>Lanchonete</InputLabel>
                  <Select
                    name="lanchonete_id"
                    value={formData.lanchonete_id}
                    onChange={handleInputChange}
                    label="Lanchonete"
                  >
                    {lanchonetes.map(lanchonete => (
                      <MenuItem key={lanchonete.id} value={lanchonete.id}>
                        {lanchonete.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingCategoria ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Categorias;
